import { t } from 'i18next';
import { Lock, Pencil, Zap } from 'lucide-react';
import { Trans } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from '@/components/ui/dialog';
import { piecesHooks } from '@/features/pieces/lib/pieces-hook';
import { UpgradeButton } from '@/features/usage/components/upgrade-button';
import { ActionType, BlockingStep } from '@activepieces/shared';

import { useBlockingPiecesStore } from './blocking-pieces-store';

const CORE_ACTION_LABELS: Record<string, string> = {
  [ActionType.CODE]: 'Custom Javascript Code',
  [ActionType.LOOP_ON_ITEMS]: 'Loop on Items',
};

const joinWithAnd = (labels: string[]): string => {
  if (labels.length <= 1) {
    return labels[0] ?? '';
  }
  if (labels.length === 2) {
    return `${labels[0]} ${t('and')} ${labels[1]}`;
  }
  const head = labels.slice(0, -1).join(', ');
  return `${head} ${t('and')} ${labels[labels.length - 1]}`;
};

const BlockingPiecesModal = () => {
  const navigate = useNavigate();
  const { pieces } = piecesHooks.usePieces({});
  const open = useBlockingPiecesStore((s) => s.open);
  const blockingSteps = useBlockingPiecesStore((s) => s.blockingSteps);
  const flowId = useBlockingPiecesStore((s) => s.flowId);
  const setOpen = useBlockingPiecesStore((s) => s.setOpen);

  const labelForStep = (step: BlockingStep): string => {
    if (step.pieceName) {
      const meta = pieces?.find((p) => p.name === step.pieceName);
      return (
        meta?.displayName ?? step.pieceName.replace('@activepieces/piece-', '')
      );
    }
    return t(CORE_ACTION_LABELS[step.type] ?? 'Unknown');
  };

  const distinctLabels = Array.from(new Set(blockingSteps.map(labelForStep)));
  const count = distinctLabels.length;
  const joinedLabels = joinWithAnd(distinctLabels);

  const handleEditFlow = () => {
    setOpen(false);
    navigate(`/flows/${flowId}`);
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="sm:max-w-[640px]">
        <div className="flex flex-col items-center gap-3 text-center">
          <div className="bg-[#55c96b]/10 text-[#55c96b] p-3 rounded-2xl">
            <Lock className="h-6 w-6" />
          </div>
          <DialogTitle className="text-lg font-semibold">
            {t('This flow cannot be enabled with your Smart plan')}
          </DialogTitle>
          <DialogDescription className="text-sm text-muted-foreground">
            <Trans
              i18nKey="{count, plural, one {It uses # feature — <strong>{pieces}</strong> — that is not in your Smart plan. What would you like to do?} other {It uses # features — <strong>{pieces}</strong> — that are not in your Smart plan. What would you like to do?}}"
              values={{ count, pieces: joinedLabels }}
            />
          </DialogDescription>
        </div>

        <div className="flex gap-4 my-5">
          <div className="flex-1 rounded-xl border p-5 flex flex-col gap-3">
            <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
              <Pencil className="h-4 w-4" />
              {t('Stay on Smart')}
            </div>
            <h3 className="text-lg font-semibold">{t('Edit this flow')}</h3>
            <p className="text-sm text-muted-foreground">
              {t(
                '{count, plural, one {Open the flow and remove the # Business feature, then publish.} other {Open the flow and remove the # Business features, then publish.}}',
                { count },
              )}
            </p>
            <div className="flex flex-wrap gap-2">
              {distinctLabels.map((label) => (
                <Badge key={label} variant="secondary">
                  {label}
                </Badge>
              ))}
            </div>
            <Button
              variant="outline"
              className="w-full mt-auto"
              onClick={handleEditFlow}
            >
              {t('Edit this flow')}
            </Button>
          </div>

          <div className="flex-1 rounded-xl border-2 border-[#55c96b] p-5 flex flex-col gap-3">
            <div className="flex items-center gap-2 text-sm font-medium text-[#55c96b]">
              <Zap className="h-4 w-4 text-[#55c96b]" />
              {t('Keep everything')}
            </div>
            <h3 className="text-lg font-semibold">
              {t('Upgrade to Business plan')}
            </h3>
            <p className="text-sm text-muted-foreground">
              {t(
                'Publish this flow as-is and unlock all advanced features and steps.',
              )}
            </p>
            <div className="flex flex-wrap gap-2">
              {distinctLabels.map((label) => (
                <Badge key={label} variant="secondary">
                  {label}
                </Badge>
              ))}
              <Badge variant="secondary">{t('+ more')}</Badge>
            </div>
            <div className="mt-auto">
              <UpgradeButton />
            </div>
          </div>
        </div>

        <div className="flex justify-center">
          <Button variant="ghost" onClick={() => setOpen(false)}>
            {t('Not now')}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

BlockingPiecesModal.displayName = 'BlockingPiecesModal';
export { BlockingPiecesModal };
